import { createSelector } from "@reduxjs/toolkit";
import { IFC_CLASSES_CEILING_DEVICES } from "@multiconsult-group/multiplacer-core";
import { IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import type { GetMcbimItemsInSpaceApiArg, GetMcbimItemsInSpaceApiResponse } from "../../../../openapi";
import { openapi } from "../../../../openapi";
import { IRootState } from "../../../app/store";
import { buildCeilingGridPixPathsFromLines } from "../../../buildCeilingGridPixPathsFromLines";
import { convertDevicesToPixPolygons } from "../../../DesignTab/devices/deviceUtils";
import { selectHttpDevicesInRoom } from "../../../DesignTab/devices/deviceSlice";
import { selectCompCodePath, selectProjectNo } from "../../../globalSelectors";
import { selectActivePartialMcbimSpace } from "../../../McBim/mcbimSelectors";
import { selectHttpMcBimItemsInSelectedRoom } from "../../singleRoomSelectors";
import { formatMcBimItemsToPixPolygons, PartialMcBimSpace } from "../../../utils";
import {
  selectSelectedSimilarRoomWithIfcDesign,
  selectSimilarRoomsWithIfcDesignCurrentRoomName,
} from "./similarRoomsWithIfcDesignSelectors";

const EMPTY_MCBIM_ITEMS: GetMcbimItemsInSpaceApiResponse = [];

const isVisibleIfcDesignItem = (item: GetMcbimItemsInSpaceApiResponse[number]): boolean =>
  item.instanceClass === "IfcFurniture" ||
  IFC_CLASSES_CEILING_DEVICES.includes(
    item.instanceClass as (typeof IFC_CLASSES_CEILING_DEVICES)[number]
  );

export const selectSimilarRoomWithIfcDesignViewerMcbimItemsArgs = createSelector(
  [selectProjectNo, selectSelectedSimilarRoomWithIfcDesign],
  (projectNo, room): GetMcbimItemsInSpaceApiArg | null => {
    if (!projectNo || !room) {
      return null;
    }

    return {
      projectNo,
      spaceName: room.roomNumber,
    };
  }
);

const selectSimilarRoomWithIfcDesignMcbimItems = createSelector(
  [selectSimilarRoomWithIfcDesignViewerMcbimItemsArgs, (state: IRootState) => state],
  (args, state): GetMcbimItemsInSpaceApiResponse => {
    if (!args) {
      return EMPTY_MCBIM_ITEMS;
    }

    return openapi.endpoints.getMcbimItemsInSpace.select(args)(state).data ?? EMPTY_MCBIM_ITEMS;
  }
);

export const selectSimilarRoomWithIfcDesignSourceMcbimItems = createSelector(
  [selectSimilarRoomWithIfcDesignMcbimItems],
  (mcbimItems): GetMcbimItemsInSpaceApiResponse =>
    mcbimItems.filter((item) =>
      IFC_CLASSES_CEILING_DEVICES.includes(
        item.instanceClass as (typeof IFC_CLASSES_CEILING_DEVICES)[number]
      )
    )
);

const selectSimilarRoomWithIfcDesignPixPolygons = createSelector(
  [selectSelectedSimilarRoomWithIfcDesign, selectSimilarRoomsWithIfcDesignCurrentRoomName],
  (room, roomName): IPixPolygon[] => {
    if (!room) {
      return [];
    }

    return [
      {
        id: room.roomNumber,
        points: room.polygon as number[][],
        fillColor: "#ffffff",
        strokeColor: "#000000",
        strokeWidth: "10",
        text: {
          values: [room.roomNumber, roomName ?? "-"],
          color: "#000000",
          fontSize: "1",
          show: false,
        },
        isMoveable: false,
        tooltip: undefined,
        isDragSelectable: false,
      },
    ];
  }
);

const selectSimilarRoomWithIfcDesignItemPixPolygons = createSelector(
  [selectSimilarRoomWithIfcDesignMcbimItems, selectCompCodePath],
  (mcbimItems, compCodePath): IPixPolygon[] => {
    if (!compCodePath) {
      return [];
    }

    return formatMcBimItemsToPixPolygons({
      mcBimItems: mcbimItems.filter(isVisibleIfcDesignItem),
      compCodePath,
      showDuctSegment: false,
    });
  }
);

export const selectSelectedSimilarRoomWithIfcDesignAsMcbimSpace = createSelector(
  [selectSelectedSimilarRoomWithIfcDesign, selectSimilarRoomsWithIfcDesignCurrentRoomName],
  (room, roomName): PartialMcBimSpace | null => {
    if (!room) {
      return null;
    }

    return {
      name: room.roomNumber,
      longName: roomName,
      polygon: room.polygon,
      ceilingGrids: room.ceilingGrids,
      buildingStory: room.floor
        ? {
            globalId: room.floor,
            name: room.floor,
          }
        : null,
    } as PartialMcBimSpace;
  }
);

export const selectSimilarRoomWithIfcDesignViewerPixPolygons = createSelector(
  [selectSimilarRoomWithIfcDesignPixPolygons, selectSimilarRoomWithIfcDesignItemPixPolygons],
  (roomPolygons, itemPolygons): IPixPolygon[] => [...roomPolygons, ...itemPolygons]
);

export const selectSimilarRoomWithIfcDesignViewerCeilingGridPixPaths = createSelector(
  [selectSelectedSimilarRoomWithIfcDesign],
  (room) =>
    buildCeilingGridPixPathsFromLines(
      room
        ? [
            {
              name: room.roomNumber,
              globalId: room.roomNumber,
              buildingStory: null,
              fileName: "",
              polygon: room.polygon,
              ceilingGrids: room.ceilingGrids,
            },
          ]
        : []
    )
);

export const selectSimilarRoomWithIfcDesignViewerZoomExtentKey = createSelector(
  [
    selectSelectedSimilarRoomWithIfcDesign,
    selectSimilarRoomWithIfcDesignItemPixPolygons,
    selectSimilarRoomWithIfcDesignViewerCeilingGridPixPaths,
  ],
  (room, itemPolygons, ceilingGridPaths): string =>
    room
      ? `similar-room-with-ifc-design-${room.roomNumber}-${itemPolygons.length}-${ceilingGridPaths.length}`
      : "similar-room-with-ifc-design-empty"
);

const selectCurrentRoomIfcDesignPixPolygons = createSelector(
  [selectActivePartialMcbimSpace, selectSimilarRoomsWithIfcDesignCurrentRoomName],
  (space, roomName): IPixPolygon[] => {
    if (!space?.polygon || !space.name) {
      return [];
    }

    return [
      {
        id: space.name,
        points: space.polygon as number[][],
        fillColor: "#ffffff",
        strokeColor: "#000000",
        strokeWidth: "10",
        text: {
          values: [space.name, roomName ?? "-"],
          color: "#000000",
          fontSize: "1",
          show: false,
        },
        isMoveable: false,
        tooltip: undefined,
        isDragSelectable: false,
      },
    ];
  }
);

export const selectCurrentRoomIfcDesignItemCount = createSelector(
  [selectHttpMcBimItemsInSelectedRoom],
  (mcbimItems): number =>
    mcbimItems.filter((item) =>
      IFC_CLASSES_CEILING_DEVICES.includes(
        item.instanceClass as (typeof IFC_CLASSES_CEILING_DEVICES)[number]
      )
    ).length
);

const selectCurrentRoomFurniturePixPolygons = createSelector(
  [selectHttpMcBimItemsInSelectedRoom, selectCompCodePath],
  (mcbimItems, compCodePath): IPixPolygon[] => {
    if (!compCodePath) {
      return [];
    }

    return formatMcBimItemsToPixPolygons({
      mcBimItems: mcbimItems.filter((item) => item.instanceClass === "IfcFurniture"),
      compCodePath,
      showDuctSegment: false,
    });
  }
);

const selectCurrentRoomMultiplacerDevicePixPolygons = createSelector(
  [
    selectHttpDevicesInRoom,
    selectActivePartialMcbimSpace,
    (state: IRootState) => state.design.nonPublischedDevicesFromAlgos,
  ],
  (savedDevices, activeSpace, nonPublishedDevices): IPixPolygon[] => {
    const previewDevices = activeSpace?.name
      ? nonPublishedDevices.filter((device) => device.roomNumber === activeSpace.name)
      : [];

    return convertDevicesToPixPolygons({
      devices: [...savedDevices, ...previewDevices],
      typeDevices: [],
      selectedDeviceId: null,
      hideDevices: false,
      showTrueSize: false,
      isMoveable: false,
    });
  }
);

export const selectCurrentRoomIfcDesignViewerPixPolygons = createSelector(
  [
    selectCurrentRoomIfcDesignPixPolygons,
    selectCurrentRoomFurniturePixPolygons,
    selectCurrentRoomMultiplacerDevicePixPolygons,
  ],
  (roomPolygons, furniturePolygons, devicePolygons): IPixPolygon[] => [
    ...roomPolygons,
    ...furniturePolygons,
    ...devicePolygons,
  ]
);

export const selectCurrentRoomIfcDesignViewerCeilingGridPixPaths = createSelector(
  [selectActivePartialMcbimSpace],
  (space) => buildCeilingGridPixPathsFromLines(space ? [space] : [])
);

export const selectCurrentRoomIfcDesignViewerZoomExtentKey = createSelector(
  [
    selectActivePartialMcbimSpace,
    selectCurrentRoomFurniturePixPolygons,
    selectCurrentRoomMultiplacerDevicePixPolygons,
    selectCurrentRoomIfcDesignViewerCeilingGridPixPaths,
  ],
  (space, furniturePolygons, devicePolygons, ceilingGridPaths): string =>
    space?.name
      ? `current-room-ifc-design-${space.name}-${furniturePolygons.length}-${devicePolygons.length}-${ceilingGridPaths.length}`
      : "current-room-ifc-design-empty"
);

export const selectCopySimilarRoomWithIfcDesignToCurrentRoomState = createSelector(
  [
    selectSelectedSimilarRoomWithIfcDesignAsMcbimSpace,
    selectActivePartialMcbimSpace,
    selectSimilarRoomWithIfcDesignSourceMcbimItems,
    selectHttpDevicesInRoom,
    (state: IRootState) => state.design.nonPublischedDevicesFromAlgos,
  ],
  (sourceSpace, targetSpace, sourceItems, targetDevices, nonPublishedDevices) => {
    if (!sourceSpace || !targetSpace) {
      return {
        disabled: true,
        showCopyButton: false,
        hint: "Velg et rom å kopiere fra",
      };
    }

    if (sourceSpace.name === targetSpace.name) {
      return {
        disabled: true,
        showCopyButton: false,
        hint: "Samme rom kan ikke kopieres",
      };
    }

    if (sourceItems.length === 0) {
      return {
        disabled: true,
        showCopyButton: true,
        hint: "Laster IFC-komponenter fra valgt rom",
      };
    }

    if (targetDevices.length > 0) {
      return {
        disabled: true,
        showCopyButton: false,
        hint: "Aktuelt rom har allerede komponenter",
      };
    }

    const existingPreviewDevices = nonPublishedDevices.filter(
      (device) => device.roomNumber === targetSpace.name
    );

    if (existingPreviewDevices.length > 0) {
      return {
        disabled: true,
        showCopyButton: false,
        hint: "Aktuelt rom har allerede forhåndskopierte komponenter",
      };
    }

    return {
      disabled: false,
      showCopyButton: true,
      hint: "Klar til å kopiere IFC-komponenter",
    };
  }
);
